// MANISH CUSTOM CODE - SHOP BY BUDGET (manish-cc) - START
// Powers sections/manish-cc-budget-shop.liquid.
//
// Filtering: every product card is already in the page. Each card knows which
// budget ranges its price falls into (data-ranges="0 2" = range 0 and range
// 2), and every tab knows its range number (data-key). Clicking a tab shows
// the cards whose ranges include that number (the "All" tab shows every
// card) and hides the rest, then updates the status line, the optional
// product count and the empty-state message. Nothing is fetched, so
// switching tabs is instant.
//
// Tabs follow the standard tab pattern: Left / Right / Home / End move
// between tabs, only the selected tab is in the Tab order, and the panel is
// labelled by the selected tab.
//
// Variant dropdown (optional, products with several variants): choosing a
// variant updates the hidden variant id that the add-to-cart form submits,
// disables the button for sold-out variants and updates the price shown.
//
// Carousel layout (the default): the same product list becomes one scrolling
// row (a native scroll-snap strip, so touch swipe, trackpad and keyboard
// scrolling all work with no extra code). This file adds the previous / next
// arrows. They only show when the OPEN TAB has more products than fit: each
// click moves the row by exactly ONE product, revealing the next hidden one,
// and an arrow that can't go further disappears. Switching budget tabs sends
// the strip back to the start and re-checks, so a tab with few products shows
// no arrows and a tab with many shows them. Optional dots (one per page of
// products) jump a whole page at a time. Everything is recalculated whenever
// the visible products or the screen size change.
//
// Add to cart: the card's form is wrapped in the theme's own
// <product-form-component>, which performs the add and updates the cart
// drawer. This file only adds the button feedback (Adding... / Added) by
// listening for the theme's cart-update event. If that event API can't be
// loaded the button simply returns to normal after a moment.

var initialized = new WeakSet();

function reducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function setupCarousel(root) {
  if (root.dataset.layout !== 'carousel') return null;

  var track = root.querySelector('[data-mm-sb-track]');
  if (!track) return null;

  var prev = root.querySelector('[data-mm-sb-prev]');
  var next = root.querySelector('[data-mm-sb-next]');
  var dotsEl = root.querySelector('[data-mm-sb-dots]');
  var dots = [];
  var pageCount = 0;
  var queued = false;

  function maxScroll() {
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  function gapSize() {
    var style = window.getComputedStyle(track);
    return parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
  }

  // Distance of one arrow click: exactly one product plus the gap.
  function cardStep() {
    var item = Array.prototype.filter.call(track.children, function (el) {
      return !el.hidden && el.hasAttribute('data-mm-sb-item');
    })[0];
    if (!item || !item.offsetWidth) return track.clientWidth || 1;
    return item.offsetWidth + gapSize();
  }

  // Distance covered by one dot: as many whole products as fit.
  function pageSize() {
    var item = Array.prototype.filter.call(track.children, function (el) {
      return !el.hidden && el.hasAttribute('data-mm-sb-item');
    })[0];
    if (!item || !item.offsetWidth) return track.clientWidth || 1;
    var gap = gapSize();
    var step = item.offsetWidth + gap;
    var perPage = Math.max(1, Math.floor((track.clientWidth + gap + 1) / step));
    return perPage * step;
  }

  function smooth() {
    return reducedMotion() ? 'auto' : 'smooth';
  }

  function goToPage(index) {
    var target = Math.min(index * pageSize(), maxScroll());
    track.scrollTo({ left: target, behavior: smooth() });
  }

  function buildDots(count) {
    dotsEl.textContent = '';
    dots = [];
    for (var i = 0; i < count; i += 1) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mm-sb__dot';
      dot.setAttribute('aria-label', 'Go to page ' + (i + 1) + ' of ' + count);
      (function (index) {
        dot.addEventListener('click', function () { goToPage(index); });
      })(i);
      dotsEl.appendChild(dot);
      dots.push(dot);
    }
    pageCount = count;
  }

  function update() {
    queued = false;
    var max = maxScroll();
    var left = track.scrollLeft;

    if (prev) prev.disabled = left <= 2;
    if (next) next.disabled = left >= max - 2;

    // Put the arrows level with the middle of the pictures.
    var media = track.querySelector('[data-mm-sb-item]:not([hidden]) .mm-sb__media');
    if (media && media.offsetHeight) {
      var viewport = track.parentElement;
      var offset = media.getBoundingClientRect().top - viewport.getBoundingClientRect().top;
      root.style.setProperty('--mm-sb-arrow-top', Math.round(offset + media.offsetHeight / 2) + 'px');
    }

    if (dotsEl) {
      var pages = max <= 2 ? 1 : Math.ceil(max / pageSize()) + 1;
      if (pages !== pageCount) buildDots(pages);
      dotsEl.hidden = pages <= 1;

      var active = pages > 1 ? Math.round((left / max) * (pages - 1)) : 0;
      dots.forEach(function (dot, i) {
        var on = i === active;
        dot.classList.toggle('is-active', on);
        if (on) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
  }

  function queueUpdate() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(update);
  }

  if (prev) prev.addEventListener('click', function () {
    track.scrollBy({ left: -cardStep(), behavior: smooth() });
  });
  if (next) next.addEventListener('click', function () {
    track.scrollBy({ left: cardStep(), behavior: smooth() });
  });

  track.addEventListener('scroll', queueUpdate, { passive: true });
  window.addEventListener('resize', queueUpdate);
  window.addEventListener('load', queueUpdate);
  if (window.ResizeObserver) new window.ResizeObserver(queueUpdate).observe(track);

  update();

  return {
    // Called after the visible products change (a tab was chosen).
    reset: function () {
      track.scrollLeft = 0;
      update();
    }
  };
}

function initBudgetShop(root) {
  if (initialized.has(root)) return;
  initialized.add(root);

  var carousel = setupCarousel(root);

  var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-mm-sb-tab]'));
  var items = Array.prototype.slice.call(root.querySelectorAll('[data-mm-sb-item]'));
  var panel = root.querySelector('[data-mm-sb-panel]');
  var rangeEl = root.querySelector('[data-mm-sb-status-range]');
  var countEl = root.querySelector('[data-mm-sb-count]');
  var emptyEl = root.querySelector('[data-mm-sb-empty]');

  var showCount = root.dataset.showCount === 'true';
  var countOne = root.dataset.countOne || 'product';
  var countMany = root.dataset.countMany || 'products';
  var scrollTabs = root.dataset.tabsMobile === 'scroll';

  function itemMatches(item, key) {
    if (key === 'all') return true;
    var ranges = (item.dataset.ranges || '').split(' ').filter(Boolean);
    return ranges.indexOf(key) !== -1;
  }

  function apply(key, label, animate) {
    var visible = 0;
    items.forEach(function (item) {
      var show = itemMatches(item, key);
      var wasHidden = item.hidden;
      item.hidden = !show;
      if (show) {
        visible += 1;
        if (animate && wasHidden && !reducedMotion()) {
          item.classList.remove('is-in');
          // restart the animation
          void item.offsetWidth;
          item.classList.add('is-in');
        }
      } else {
        item.classList.remove('is-in');
      }
    });

    if (rangeEl && label) rangeEl.textContent = label;

    if (countEl) {
      countEl.textContent = showCount
        ? ' \u00b7 ' + visible + ' ' + (visible === 1 ? countOne : countMany)
        : '';
    }

    if (emptyEl) emptyEl.hidden = visible !== 0;

    if (carousel) carousel.reset();
  }

  function select(tab, options) {
    options = options || {};
    tabs.forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
    });

    if (panel) panel.setAttribute('aria-labelledby', tab.id);

    apply(tab.dataset.key, tab.dataset.label, options.animate !== false);

    if (options.focus) tab.focus();

    if (scrollTabs && options.scroll !== false && tab.scrollIntoView) {
      try {
        tab.scrollIntoView({
          behavior: reducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      } catch (e) {
        /* older browsers: ignore */
      }
    }
  }

  tabs.forEach(function (tab, index) {
    tab.addEventListener('click', function () {
      select(tab);
    });

    tab.addEventListener('keydown', function (event) {
      var next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = tabs[(index + 1) % tabs.length];
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = tabs[(index - 1 + tabs.length) % tabs.length];
      } else if (event.key === 'Home') {
        next = tabs[0];
      } else if (event.key === 'End') {
        next = tabs[tabs.length - 1];
      }
      if (next) {
        event.preventDefault();
        select(next, { focus: true });
      }
    });
  });

  // Settle the starting state (counts, empty message) without animating.
  var current = tabs.filter(function (t) { return t.classList.contains('is-active'); })[0] || tabs[0];
  if (current) {
    select(current, { animate: false, scroll: false });
  } else if (emptyEl) {
    emptyEl.hidden = items.length !== 0;
  }

  // Variant dropdowns
  root.querySelectorAll('[data-mm-sb-variant]').forEach(function (dropdown) {
    dropdown.addEventListener('change', function () {
      var form = dropdown.closest('form');
      var option = dropdown.options[dropdown.selectedIndex];
      if (!form || !option) return;

      var idInput = form.querySelector('input[name="id"]');
      if (idInput) idInput.value = option.value;

      var button = form.querySelector('[data-mm-sb-btn]');
      if (button) button.disabled = option.dataset.available === 'false';

      var card = dropdown.closest('.mm-sb__card');
      var priceNow = card && card.querySelector('.mm-sb__price-now');
      if (priceNow && option.dataset.price) priceNow.textContent = option.dataset.price;
    });
  });
}

// ---- Add to cart button feedback (one listener for the whole page) ----

var resetTimers = new WeakMap();

function setButtonState(button, state) {
  var text = button.dataset.textIdle;
  if (state === 'adding') text = button.dataset.textAdding || text;
  if (state === 'added') text = button.dataset.textAdded || text;

  button.textContent = text;
  button.dataset.state = state;
  button.disabled = state === 'adding';

  var timer = resetTimers.get(button);
  if (timer) window.clearTimeout(timer);

  if (state === 'added' || state === 'error') {
    resetTimers.set(button, window.setTimeout(function () {
      setButtonState(button, 'idle');
    }, 1600));
  } else if (state === 'adding') {
    // Safety net: never leave a button stuck on "Adding..."
    resetTimers.set(button, window.setTimeout(function () {
      setButtonState(button, 'idle');
    }, 8000));
  }
}

function buttonForForm(form) {
  return form && form.querySelector ? form.querySelector('[data-mm-sb-btn]') : null;
}

var cartEventsReady = false;

document.addEventListener('submit', function (event) {
  var form = event.target && event.target.closest ? event.target.closest('[data-mm-sb-form]') : null;
  var button = buttonForForm(form);
  if (!button || button.disabled) return;
  setButtonState(button, 'adding');

  // Without the theme's cart event we can't know the outcome: settle quickly.
  if (!cartEventsReady) {
    resetTimers.set(button, window.setTimeout(function () {
      setButtonState(button, 'idle');
    }, 1200));
  }
});

function listenForCartResult(eventName) {
  cartEventsReady = true;
  document.addEventListener(eventName, function (event) {
    var host = event.target && event.target.closest ? event.target.closest('[data-mm-sb-card-form]') : null;
    var button = buttonForForm(host);
    if (!button || !event.promise) return;

    event.promise.then(function (result) {
      var failed = result && result.detail && result.detail.didError;
      setButtonState(button, failed ? 'error' : 'added');
    }).catch(function () {
      setButtonState(button, 'error');
    });
  });
}

try {
  import('@shopify/events').then(function (events) {
    if (events && events.StandardEvents && events.StandardEvents.cartLinesUpdate) {
      listenForCartResult(events.StandardEvents.cartLinesUpdate);
    }
  }).catch(function () {
    /* feedback falls back to the timed reset above */
  });
} catch (e) {
  /* dynamic import unsupported: keep the timed reset */
}

function initAll() {
  document.querySelectorAll('[data-mm-sb]').forEach(initBudgetShop);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
document.addEventListener('shopify:section:load', initAll);
// MANISH CUSTOM CODE - SHOP BY BUDGET (manish-cc) - END
